<template>
  <div
    class="bg-static-info-mid rounded-sm flex items-center gap-12 px-12 py-12"
  >
    <IconMoney size="large" />

    <div>
      <h2 class="body-1-bold">{{ i18n(translations.pricingStrategyTitle) }}</h2>
      <span class="body-2">
        {{
          i18n(translations.pricingStrategyDescription, {
            price: i18n.price(pricingStrategy.salesMaximizerPrice!),
            demand: pricingStrategy.estimatedDemand,
          })
        }}
      </span>
    </div>

    <RevButton
      :loading="isUpdatingPricingStrategy"
      size="small"
      variant="primary"
      @click="handleUpdatePricingStrategy"
    >
      {{ i18n(translations.pricingStrategyUpdate) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api'
import type {
  Listing,
  MarketListing,
  PricingStrategy,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { IconMoney } from '@ds/icons/IconMoney'

import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import translations from '../MarketList.translations'

const { openErrorToast } = useTheToast()
const { setMaximizerPrice, formatListingToTrackingEvent } = useListings()
const tracking = useTracking()
const i18n = useI18n()

const props = defineProps<{
  pricingStrategy: PricingStrategy
  market: MarketListing & { market: MarketCountryCode }
  listing: Listing
}>()

const isUpdatingPricingStrategy = ref(false)

async function handleUpdatePricingStrategy() {
  try {
    isUpdatingPricingStrategy.value = true

    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_price_update',
      name: 'optimal_price',
      ...formatListingToTrackingEvent(props.listing, {
        ...props.market,
        market: props.market.market,
      }),
    })

    await setMaximizerPrice(props.listing.id, props.market.market)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isUpdatingPricingStrategy.value = false
  }
}
</script>
